/**
 * LES CONNECTEURS ACTIFS : les pieces d'Activepieces que Flux sait executer,
 * par le PONT (`tools/pieces/`, service separe en ligne, charge dans le
 * processus des Functions en local et en test). Doctrine :
 * `docs/socle/flux.md`, section « Connecteurs Activepieces ».
 *
 * UNE ENTREE PAR CONNECTEUR, rien d'autre a ecrire pour en activer un de plus :
 *   - `id` : l'id du pont (cle de `tools/pieces/pieces.json`), cite par une
 *     etape ou un declencheur `connecteur` (`flux.ts`) ;
 *   - `idActivepieces` : son dossier dans `packages/pieces/community`, celui du
 *     catalogue « Bientôt » (`apps/hello/public/connecteurs/catalogue.json`) :
 *     l'ecran s'en sert pour ne pas le montrer deux fois, et pour son logo ;
 *   - `fournisseur` : l'id du registre des connexions (`connexions.ts`) dont la
 *     connexion l'authentifie, `null` si la piece n'en demande pas ;
 *   - `classement` : chaque action, `lecture`, `ecriture` ou `suppression`.
 *     Repris de la `classification` qu'Activepieces declare sur chaque action
 *     (READ et SEARCH : lecture, WRITE : ecriture, DESTRUCTIVE : suppression).
 *     Une action absente compte comme `ecriture` (le defaut prudent). Le
 *     classement fixe le niveau d'acces demande a la connexion (lecture, ou
 *     ecriture) et les bornes d'un canal (option B : un canal restreint ne pose
 *     que des lectures, Lia jamais une suppression) ;
 *   - `declencheurs` : ceux que Flux sait tenir, avec leur strategie. Seulement
 *     le SONDAGE (`POLLING`, chaque minute) et le WEBHOOK simple (une adresse
 *     enregistree a l'activation). Pas `APP_WEBHOOK` (une application par
 *     editeur, Slack), ni un webhook qui se renouvelle (Google Sheets).
 *
 * Ce fichier est bundle vers les Functions (`functions/build-studio.mjs`).
 * Les noms d'actions et leur classification viennent des pieces d'Activepieces
 * (MIT, Copyright (c) 2020-2024 Activepieces Inc., notice complete :
 * `apps/hello/src/apps/flux/NOTICE-ACTIVEPIECES.md`).
 */

/** Ce qu'une action fait chez le fournisseur. */
export const CLASSEMENTS_ACTION_CONNECTEUR = ["lecture", "ecriture", "suppression"] as const;
export type ClassementActionConnecteur = (typeof CLASSEMENTS_ACTION_CONNECTEUR)[number];

/** Les strategies de declencheur que Flux tient. */
export const STRATEGIES_DECLENCHEUR_CONNECTEUR = ["POLLING", "WEBHOOK"] as const;
export type StrategieDeclencheurConnecteur = (typeof STRATEGIES_DECLENCHEUR_CONNECTEUR)[number];

export interface ConnecteurActif {
  /** L'id du pont : `google-sheets`, `brevo`... */
  id: string;
  libelle: string;
  /** Une ligne. */
  description: string;
  /** Le dossier de la piece chez Activepieces (`brevo` y est `sendinblue`). */
  idActivepieces: string;
  /** Relatif a `/connecteurs/` de l'app (`apps/hello/public/connecteurs/logos/`). */
  logo: string;
  /** L'id du fournisseur de connexion qui l'authentifie, ou `null`. */
  fournisseur: string | null;
  /** Les actions classees ; une action absente vaut `ecriture`. */
  classement: {
    lecture: readonly string[];
    ecriture: readonly string[];
    suppression: readonly string[];
  };
  /** Les declencheurs permis, par nom, avec leur strategie. */
  declencheurs: Readonly<Record<string, StrategieDeclencheurConnecteur>>;
}

export const CONNECTEURS_ACTIFS: readonly ConnecteurActif[] = [
  {
    id: "google-sheets",
    libelle: "Google Sheets",
    description: "Lire, ajouter et mettre à jour des lignes de tableur.",
    idActivepieces: "google-sheets",
    logo: "logos/google-sheets.webp",
    fournisseur: "google_sheets",
    classement: {
      lecture: [
        "export_sheet", "find_row_by_num", "find_rows", "find_spreadsheets", "find-worksheet", "get_next_rows",
        "get-many-rows", "read-data-range", "sheets_export_worksheet", "sheets_find_rows", "sheets_find_worksheet",
        "sheets_get_all_rows", "sheets_get_next_rows", "sheets_get_row", "sheets_get_spreadsheet", "sheets_get_values",
        "sheets_search_spreadsheets",
      ],
      ecriture: [
        "copy-worksheet", "create-column", "create-spreadsheet", "create-worksheet", "find-or-create-row",
        "find-or-create-worksheet", "format-row", "google-sheets-insert-multiple-rows", "insert_row",
        "insert-row-at-top", "rename-worksheet", "sheets_add_column", "sheets_add_multiple_rows", "sheets_add_row",
        "sheets_add_row_at_top", "sheets_add_worksheet", "sheets_append_dimension", "sheets_append_values",
        "sheets_auto_resize_dimensions", "sheets_copy_worksheet", "sheets_create_spreadsheet",
        "sheets_find_or_create_row", "sheets_find_or_create_worksheet", "sheets_format_cells",
        "sheets_insert_dimension", "sheets_rename_worksheet", "sheets_update_dimension_properties",
        "sheets_update_multiple_rows", "sheets_update_row", "sheets_update_sheet_properties", "sheets_update_values",
        "update_row", "update-multiple-rows",
      ],
      suppression: [
        "clear_sheet", "clear-rows", "delete_row", "delete-multiple-rows", "delete-worksheet", "sheets_clear_values",
        "sheets_delete_dimension", "sheets_delete_multiple_rows", "sheets_delete_row", "sheets_delete_worksheet",
      ],
    },
    declencheurs: {
      "new-spreadsheet": "POLLING", "new-worksheet": "POLLING",
    },
  },
  {
    id: "google-drive",
    libelle: "Google Drive",
    description: "Déposer, lire et partager des fichiers.",
    idActivepieces: "google-drive",
    logo: "logos/google-drive.webp",
    fournisseur: "google_drive",
    classement: {
      lecture: [
        "drive_download_file", "drive_export_folder_as_zip", "drive_export_workspace_file", "drive_get_about",
        "drive_get_file", "drive_get_reply", "drive_get_shared_drive", "drive_list_comments", "drive_list_files",
        "drive_list_permissions", "drive_list_replies", "drive_list_shared_drives", "drive_search_files",
        "get-file-or-folder-by-id", "list-files", "read-file", "search-folder",
      ],
      ecriture: [
        "create_new_gdrive_file", "create_new_gdrive_folder", "drive_copy_file", "drive_create_comment",
        "drive_create_file_from_text", "drive_create_folder", "drive_create_reply", "drive_create_shared_drive",
        "drive_move_file", "drive_replace_file_content", "drive_save_file_as_pdf", "drive_set_public_access",
        "drive_share_file", "drive_untrash_file", "drive_update_file_metadata", "drive_update_permission",
        "drive_update_reply", "drive_update_shared_drive", "drive_upload_file", "drive_upload_from_url",
        "duplicate_file", "google-drive-move-file", "save_file_as_pdf", "set_public_access", "update_permissions",
        "upload_gdrive_file",
      ],
      suppression: [
        "delete_gdrive_file", "delete_permissions", "drive_delete_file", "drive_delete_reply",
        "drive_delete_shared_drive", "drive_empty_trash", "drive_remove_permission", "drive_trash_file",
        "trash_gdrive_file",
      ],
    },
    declencheurs: {
      "new_file": "POLLING", "new_folder": "POLLING", "new_or_updated_file": "POLLING",
    },
  },
  {
    id: "microsoft-teams",
    libelle: "Microsoft Teams",
    description: "Publier dans un canal ou une conversation.",
    idActivepieces: "microsoft-teams",
    logo: "logos/microsoft-teams.webp",
    fournisseur: "microsoft_teams",
    classement: {
      lecture: [
        "microsoft_teams_find_channel", "microsoft_teams_find_team_member", "microsoft_teams_get_channel_message",
        "microsoft_teams_get_chat_message", "microsoft_teams_get_meeting_recording",
        "microsoft_teams_get_meeting_transcript",
      ],
      ecriture: [
        "microsoft_teams_create_channel", "microsoft_teams_create_chat_and_send_message",
        "microsoft_teams_create_private_channel", "microsoft_teams_reply_to_channel_message",
        "microsoft_teams_send_channel_message", "microsoft_teams_send_chat_message", "request_approval_direct_message",
        "request_approval_in_channel",
      ],
      suppression: [
        "microsoft_teams_delete_chat_message",
      ],
    },
    declencheurs: {
      "new-channel": "POLLING", "new-channel-message": "POLLING", "new-chat": "POLLING", "new-chat-message": "POLLING",
    },
  },
  {
    id: "microsoft-excel-365",
    libelle: "Microsoft Excel 365",
    description: "Lire et écrire des lignes d’un classeur.",
    idActivepieces: "microsoft-excel-365",
    logo: "logos/microsoft-excel-365.webp",
    fournisseur: "microsoft_excel",
    classement: {
      lecture: [
        "find_row", "find-workbook", "find-worksheet", "get_range", "get_table_columns", "get_table_rows",
        "get_workbooks", "get_worksheet", "get_worksheet_rows", "get_worksheets", "get-worksheet-columns",
        "getRowById", "lookup_table_column",
      ],
      ecriture: [
        "add_worksheet", "append_multiple_rows", "append_row", "append_table_rows", "convert_to_range",
        "copy_worksheet", "create_table", "create_worksheet", "createWorkbook", "rename_worksheet", "update_row",
      ],
      suppression: [
        "clear_column", "clear_range", "clear_row", "clear_worksheet", "delete_row", "delete_table", "delete_workbook",
        "delete_worksheet",
      ],
    },
    declencheurs: {
      "new_row": "POLLING", "new_row_in_table": "POLLING", "new_worksheet": "POLLING", "updated_row": "POLLING",
    },
  },
  {
    id: "slack",
    libelle: "Slack",
    description: "Publier un message, lire un canal.",
    idActivepieces: "slack",
    logo: "logos/slack.webp",
    fournisseur: "slack",
    classement: {
      lecture: [
        "find_user_by_email", "find_user_by_handle", "find-user-by-id", "get_group_by_handle", "get_user", "get-file",
        "get-message", "getChannelHistory", "list_user_groups", "list_users", "listUsers", "markdownToSlackFormat",
        "retrieveThreadMessages", "searchMessages", "slack_find_channel", "slack_get_channel_history",
        "slack_get_channel_info", "slack_get_file", "slack_get_message_permalink", "slack_get_reactions",
        "slack_get_thread_replies", "slack_list_channel_members", "slack_list_channels", "slack_list_custom_emoji",
        "slack_list_files", "slack_list_scheduled_messages", "slack_list_user_conversations",
        "slack_list_user_reactions", "slack_search_all", "slack_search_messages", "slack-find-user-by-email",
        "slack-find-user-by-handle",
      ],
      ecriture: [
        "invite-user-to-channel", "request_action_direct_message", "request_action_message",
        "request_approval_direct_message", "request_approval_message", "send_channel_message", "send_direct_message",
        "send_message_to_multiple_users", "set_user_status", "set-channel-topic", "slack_add_reaction",
        "slack_close_dm", "slack_create_channel", "slack_invite_users_to_channel", "slack_join_channel",
        "slack_leave_channel", "slack_mark_conversation_read", "slack_post_message", "slack_remove_reaction",
        "slack_remove_user_from_channel", "slack_rename_channel", "slack_schedule_message",
        "slack_send_direct_message", "slack_send_ephemeral_message", "slack_set_channel_purpose",
        "slack_set_channel_topic", "slack_unarchive_channel", "slack_update_message", "slack-add-reaction-to-message",
        "slack-create-channel", "slack-set-user-status", "slack-update-profile", "update_group_users",
        "update_profile", "updateMessage", "uploadFile",
      ],
      suppression: [
        "delete-message", "slack_archive_channel", "slack_delete_message", "slack_delete_scheduled_message",
      ],
    },
    declencheurs: {},
  },
  {
    id: "notion",
    libelle: "Notion",
    description: "Créer et mettre à jour des pages et des bases.",
    idActivepieces: "notion",
    logo: "logos/notion.webp",
    fournisseur: "notion",
    classement: {
      lecture: [
        "find_page", "get_page_comments", "getPageOrBlockChildren", "list_database_pages", "list_databases",
        "notion_find_database_item", "notion_get_block_children", "notion_get_database", "notion_get_page",
        "notion_get_page_comments", "notion_get_user", "notion_list_users", "notion_query_database", "notion_search",
        "notion-find-database-item", "retrieve_database",
      ],
      ecriture: [
        "add_comment", "append_to_page", "create_database_item", "createPage", "notion_add_comment",
        "notion_append_to_page", "notion_create_database", "notion_create_database_item", "notion_create_page",
        "notion_move_page", "notion_restore_database_item", "notion_update_block", "notion_update_database_item",
        "restore_database_item", "update_database_item",
      ],
      suppression: [
        "archive_database_item", "notion_archive_database_item", "notion_archive_page", "notion_delete_block",
        "notion_update_database_schema",
      ],
    },
    declencheurs: {
      "new_comment": "POLLING", "new_database_item": "POLLING", "updated_database_item": "POLLING", "updated_page": "POLLING",
    },
  },
  {
    id: "hubspot",
    libelle: "HubSpot",
    description: "Contacts, entreprises et affaires du CRM.",
    idActivepieces: "hubspot",
    logo: "logos/hubspot.webp",
    fournisseur: "hubspot",
    classement: {
      lecture: [
        "find-associations", "find-company", "find-contact", "find-custom-object", "find-deal", "find-line-item",
        "find-product", "find-ticket", "get-company", "get-contact", "get-custom-object", "get-deal", "get-line-item",
        "get-owner-by-email", "get-owner-by-id", "get-page", "get-pipeline-stage-details", "get-product", "get-ticket",
        "list_association_types", "list_object_properties", "list_owners", "list_pipeline_stages", "list_pipelines",
        "list_static_lists", "search_objects",
      ],
      ecriture: [
        "add_contact_to_list", "add-contact-to-workflow", "create_note", "create_task", "create-associations",
        "create-blog-post", "create-company", "create-contact", "create-custome-object", "create-deal",
        "create-line-item", "create-or-update-contact", "create-page", "create-product", "create-ticket",
        "remove-associations", "remove-contact-from-list", "update-company", "update-contact", "update-custome-object",
        "update-deal", "update-line-item", "update-product", "update-ticket", "upload-file",
      ],
      suppression: [
        "delete-page", "remove-email-subscription",
      ],
    },
    declencheurs: {
      "deal-stage-updated": "POLLING", "new-blog-article": "POLLING", "new-company": "POLLING", "new-company-property-change": "POLLING", "new-contact": "POLLING",
      "new-contact-in-list": "POLLING", "new-contact-property-change": "POLLING", "new-custom-object": "POLLING", "new-custom-object-property-change": "POLLING",
      "new-deal": "POLLING", "new-deal-property-change": "POLLING", "new-email-event": "POLLING", "new-email-subscriptions-timeline": "POLLING",
      "new-engagement": "POLLING", "new-form-submission": "POLLING", "new-line-item": "POLLING", "new-or-updated-company": "POLLING", "new-or-updated-contact": "POLLING",
      "new-or-updated-line-item": "POLLING", "new-or-updated-product": "POLLING", "new-product": "POLLING", "new-task": "POLLING", "new-ticket": "POLLING",
      "new-ticket-property-change": "POLLING",
    },
  },
  {
    id: "stripe",
    libelle: "Stripe",
    description: "Clients, paiements, factures et abonnements.",
    idActivepieces: "stripe",
    logo: "logos/stripe.webp",
    fournisseur: "stripe",
    classement: {
      lecture: [
        "find_invoice", "get_checkout_session", "get_coupon", "get_credit_note", "get_customer", "get_dispute",
        "get_event", "get_invoice", "get_payment_intent", "get_payment_link", "get_payment_method", "get_payout",
        "get_price", "get_product", "get_promotion_code", "get_refund", "get_subscription", "get_tax_rate",
        "list_balance_transactions", "list_charges", "list_checkout_sessions", "list_coupons", "list_credit_notes",
        "list_customer_payment_methods", "list_customers", "list_disputes", "list_events", "list_invoice_lines",
        "list_invoices", "list_payment_intents", "list_payment_links", "list_payouts", "list_prices", "list_products",
        "list_promotion_codes", "list_refunds", "list_subscription_items", "list_subscriptions", "list_tax_rates",
        "list_transfers", "retrieve_balance", "retrieve_charge", "retrieve_customer", "retrieve_invoice",
        "retrieve_payment_intent", "retrieve_payout", "search_charges", "search_customer", "search_customers",
        "search_invoices", "search_payment_intents", "search_prices", "search_products", "search_subscriptions",
        "search_subscriptions_ai",
      ],
      ecriture: [
        "add_invoice_lines", "attach_payment_method", "capture_payment_intent", "confirm_payment_intent",
        "create_checkout_session", "create_coupon", "create_credit_note", "create_customer", "create_customer_ai",
        "create_invoice", "create_invoice_ai", "create_invoice_item", "create_payment_intent",
        "create_payment_intent_ai", "create_payment_link", "create_payment_link_ai", "create_payment_method",
        "create_price", "create_price_ai", "create_product", "create_product_ai", "create_promotion_code",
        "create_refund", "create_refund_ai", "create_subscription", "create_subscription_ai", "create_tax_rate",
        "finalize_invoice", "send_invoice", "update_customer", "update_customer_ai", "update_dispute", "update_price",
        "update_product", "update_subscription",
      ],
      suppression: [
        "cancel_payment_intent", "cancel_subscription", "cancel_subscription_ai", "close_dispute",
        "deactivate_payment_link", "deactivate_payment_link_ai", "void_credit_note",
      ],
    },
    declencheurs: {
      "canceled_subscription": "WEBHOOK", "checkout_session_completed": "WEBHOOK", "invoice_payment_failed": "WEBHOOK", "new_charge": "WEBHOOK", "new_customer": "WEBHOOK",
      "new_dispute": "WEBHOOK", "new_invoice": "WEBHOOK", "new_payment": "WEBHOOK", "new_payment_link": "WEBHOOK", "new_refund": "WEBHOOK", "new_subscription": "WEBHOOK",
      "payment_failed": "WEBHOOK", "updated_subscription": "WEBHOOK",
    },
  },
  {
    id: "brevo",
    libelle: "Brevo",
    description: "Contacts, campagnes et e-mails transactionnels.",
    idActivepieces: "sendinblue",
    logo: "logos/brevo.webp",
    fournisseur: "brevo",
    classement: {
      lecture: [
        "find_contact", "get_account_info", "get_company", "get_contact_campaign_stats", "get_contact_list",
        "get_email_campaign", "get_email_template", "get_import_process", "get_sms_campaign", "list_companies",
        "list_contact_attributes", "list_contact_lists", "list_contacts", "list_crm_notes", "list_email_campaigns",
        "list_email_templates", "list_sender_domains", "list_senders", "list_sms_campaigns",
        "list_transactional_email_events",
      ],
      ecriture: [
        "create_company", "create_contact_list", "create_email_campaign", "create_event", "create_or_update_contact",
        "create_or_update_email_template", "create_sms_campaign", "import_contacts", "send_email_campaign_now",
        "send_transactional_email", "send_transactional_sms", "unsubscribe_contact", "update_contact",
        "update_email_campaign",
      ],
      suppression: [
        "delete_company", "delete_contact", "delete_email_template", "delete_sms_campaign",
      ],
    },
    declencheurs: {
      "contact_added_to_list": "WEBHOOK", "contact_deleted": "WEBHOOK", "contact_unsubscribed": "WEBHOOK", "contact_updated": "WEBHOOK", "email_bounced": "WEBHOOK",
      "email_clicked": "WEBHOOK", "email_delivered": "WEBHOOK", "email_opened": "WEBHOOK",
    },
  },
  {
    id: "typeform",
    libelle: "Typeform",
    description: "Formulaires et réponses.",
    idActivepieces: "typeform",
    logo: "logos/typeform.webp",
    fournisseur: "typeform",
    classement: {
      lecture: [
        "get_form", "get_form_messages", "get_image", "get_me", "get_theme", "get_workspace", "list_forms",
        "list_images", "list_responses", "list_themes", "list_workspaces",
      ],
      ecriture: [
        "create_form", "create_theme", "create_workspace", "duplicate_form", "update_choice_options", "update_form",
        "update_form_messages", "update_theme", "update_workspace", "upload_image",
      ],
      suppression: [
        "delete_form", "delete_image", "delete_responses", "delete_theme", "delete_workspace", "replace_form",
      ],
    },
    declencheurs: {
      "new_submission": "WEBHOOK",
    },
  },
];

export const IDS_CONNECTEURS_ACTIFS: readonly string[] = CONNECTEURS_ACTIFS.map((c) => c.id);

/** Le connecteur actif de cet id, ou `undefined`. */
export function connecteurActif(id: unknown): ConnecteurActif | undefined {
  return typeof id === "string" ? CONNECTEURS_ACTIFS.find((c) => c.id === id) : undefined;
}

/** Le classement d'une action ; `ecriture` pour une action inconnue (le defaut prudent). */
export function classementAction(connecteur: string | ConnecteurActif, action: string): ClassementActionConnecteur {
  const c = typeof connecteur === "string" ? connecteurActif(connecteur) : connecteur;
  if (!c) return "ecriture";
  if (c.classement.lecture.includes(action)) return "lecture";
  if (c.classement.suppression.includes(action)) return "suppression";
  return "ecriture";
}

/** Le niveau d'acces qu'une action demande a sa connexion : `lecture` pour une lecture, `ecriture` sinon. */
export function accesRequisPourAction(connecteur: string | ConnecteurActif, action: string): "lecture" | "ecriture" {
  return classementAction(connecteur, action) === "lecture" ? "lecture" : "ecriture";
}

/** La strategie d'un declencheur permis, ou `null` s'il ne l'est pas. */
export function strategieDeclencheur(connecteur: string | ConnecteurActif, declencheur: string): StrategieDeclencheurConnecteur | null {
  const c = typeof connecteur === "string" ? connecteurActif(connecteur) : connecteur;
  if (!c || !Object.hasOwn(c.declencheurs, declencheur)) return null;
  return c.declencheurs[declencheur] ?? null;
}

/** Le classement d'un connecteur, action par action, pour les actions nommees (defaut `ecriture`). */
export function classementDesActions(connecteur: string | ConnecteurActif, actions: readonly string[]): Record<string, ClassementActionConnecteur> {
  return Object.fromEntries(actions.map((a) => [a, classementAction(connecteur, a)]));
}
